/**
 * InviteMemberModal - 邀请成员弹窗
 * 用于 Owner 邀请新成员加入 Deal
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, UserPlus, Mail, Copy, Check, 
  Loader2, AlertCircle, Users, Shield 
} from 'lucide-react'
import { 
  createInvitation, 
  DealRole, 
  roleDisplayNames, 
  roleDescriptions 
} from '../../services/invitation'
import { parseApiError } from '../../services/api'

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  dealId: string
  dealName: string
  onInviteSent?: () => void
}

export default function InviteMemberModal({
  isOpen,
  onClose,
  dealId,
  dealName,
  onInviteSent
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<DealRole>('presales')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const canSubmit = isEmailValid && !isSubmitting
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    
    setIsSubmitting(true)
    setError(null)
    
    try {
      const invitation = await createInvitation({
        dealId,
        inviteeEmail: email,
        role
      })
      
      setInviteLink(invitation.inviteLink || null)
      onInviteSent?.()
    } catch (err) {
      const apiError = parseApiError(err)
      setError(apiError.message)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleCopyLink = async () => {
    if (!inviteLink) return
    
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // 降级处理
      const textarea = document.createElement('textarea')
      textarea.value = inviteLink
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  
  const handleClose = () => {
    setEmail('')
    setRole('presales')
    setError(null)
    setInviteLink(null)
    setCopied(false)
    onClose()
  }
  
  const roleOptions: { value: DealRole; label: string; description: string; icon: React.ReactNode }[] = [
    {
      value: 'presales',
      label: roleDisplayNames.presales,
      description: roleDescriptions.presales,
      icon: <Users className="w-5 h-5" />
    },
    {
      value: 'manager',
      label: roleDisplayNames.manager,
      description: roleDescriptions.manager,
      icon: <Shield className="w-5 h-5" />
    }
  ]
  
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            {/* 头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <UserPlus className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">邀请成员</h2>
                  <p className="text-sm text-gray-500">{dealName}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* 内容 */}
            <div className="p-6">
              {inviteLink ? (
                // 邀请成功，显示链接
                <div className="space-y-4">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-medium text-gray-900">邀请已发送</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      已向 <strong>{email}</strong> 发送邀请
                    </p>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">邀请链接（7天有效）</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
                      />
                      <button
                        onClick={handleCopyLink}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          copied
                            ? 'bg-green-100 text-green-700'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        }`}
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            已复制
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            复制
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleClose}
                    className="w-full py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    完成
                  </button>
                </div>
              ) : (
                // 邀请表单
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* 错误提示 */}
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                  
                  {/* 邮箱 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      被邀请者邮箱
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  {/* 角色选择 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择角色
                    </label>
                    <div className="space-y-2">
                      {roleOptions.map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                            role === option.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={option.value}
                            checked={role === option.value}
                            onChange={(e) => setRole(e.target.value as DealRole)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={role === option.value ? 'text-primary-600' : 'text-gray-400'}>
                                {option.icon}
                              </span>
                              <span className="font-medium text-gray-900">{option.label}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* 提交按钮 */}
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-colors ${
                      canSubmit
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        发送邀请
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

